/**
 * Token storage helper. The "Remember me" checkbox on the login modal
 * controls whether tokens survive a browser tab/window close:
 *
 *   remember = true  → write to localStorage  (persist across sessions)
 *   remember = false → write to sessionStorage (cleared on tab close)
 *
 * The current value of the flag is itself stored in localStorage under
 * `cs:remember` so it survives reloads in either mode (sessionStorage
 * users get this single key, which is harmless and not a token).
 *
 * Reads check both stores so callers don't need to know which mode the user
 * picked — but when the two stores DISAGREE the read fails closed and
 * returns nothing. See readToken() for why.
 */

import {
  ACCEPTED_COOKIE_NAMES,
  ACCESS_TOKEN_KEY as OWN_ACCESS_TOKEN_KEY,
  REFRESH_TOKEN_KEY,
  TOKEN_VALID_SECONDS_KEY,
  SESSION_COOKIE_NAME as COOKIE_NAME,
  SESSION_STORAGE_KEYS,
  REMEMBER_FLAG_KEY as REMEMBER_KEY,
  allCookieDeletions,
  cookieDeletions,
  decodeJwtPayload,
  storagesDisagree
} from './session-hygiene.js'

const REMEMBER_FLAG_KEY = REMEMBER_KEY

// ── Session cookie for same-origin platform pages ──────────────────────────
// The platform-dashboard pages served on this same host (/status, /docs,
// /tools, ...) are server-rendered and resolve their viewer from a cookie
// named `cs_access_token` — they cannot see localStorage/sessionStorage. So
// the access token is mirrored into that cookie. syncSessionCookie() is the
// ONE place `document.cookie` is written; it is module-private and reached
// only via writeToken() / clearToken() / clearSession() /
// restoreSessionCookie(), so the cookie cannot drift from the stored token.
// Never log the value.
//
// The dashboard accepts THREE cookie names (see ACCEPTED_COOKIE_NAMES). We
// write only `cs_access_token`, and every write deletes the other two — so
// exactly one accepted cookie exists on this origin at any time. Leaving a
// second name behind is what let a stale privileged cookie outrank the live
// session (dashboard 0.27.1 now fails closed on disagreement, which turns
// the same leftovers into a spurious "signed out" for the customer).
//
// HttpOnly is impossible from JS. This cookie therefore carries exactly the
// same exposure as the token already sitting in localStorage (any script on
// the origin can read both). The durable fix is auth-api setting an HttpOnly
// cookie on login/refresh; this mirror is the interim bridge.
export const ACCESS_TOKEN_KEY = OWN_ACCESS_TOKEN_KEY
export const SESSION_COOKIE_NAME = COOKIE_NAME
export {
  ACCEPTED_COOKIE_NAMES,
  REFRESH_TOKEN_KEY,
  SESSION_STORAGE_KEYS,
  TOKEN_VALID_SECONDS_KEY
}

/** Lower-cased identity a JWT carries (`sub`, `username` or `email`), or ''. */
function jwtIdentity(token) {
  const payload = decodeJwtPayload(token)
  if (!payload) return ''
  const id = payload.sub || payload.username || payload.email || ''
  return String(id).trim().toLowerCase()
}

/** Lower-cased e-mail/username of the user the persisted store remembers, or ''. */
function persistedUserIdentity(storage) {
  try {
    const raw = storage.getItem('vuex-persist')
    if (!raw) return ''
    const user = JSON.parse(raw)?.auth?.user || {}
    return String(user.email || user.username || '').trim().toLowerCase()
  } catch {
    return ''
  }
}

/**
 * One-time adoption of a session stored under the pre-2026-10-22 names
 * (`access_token`, `refresh_token`, `token_valid_seconds`).
 *
 * Those names are SHARED with the cluster-api console on this origin, so a
 * legacy `access_token` may be somebody else's session. It is adopted ONLY
 * when the token's identity equals the user this app's own persisted store
 * remembers — i.e. it is demonstrably the session this app wrote. Anything
 * else is left where it is and simply never read again. Idempotent: a nop
 * once the namespaced key exists.
 */
export function migrateLegacySessionKeys() {
  for (const storage of [localStorage, sessionStorage]) {
    try {
      if (storage.getItem(OWN_ACCESS_TOKEN_KEY)) return false
    } catch { return false }
  }
  for (const storage of [localStorage, sessionStorage]) {
    let legacy = null
    try { legacy = storage.getItem('access_token') } catch { legacy = null }
    if (!legacy) continue
    const who = jwtIdentity(legacy)
    const mine = persistedUserIdentity(storage) || persistedUserIdentity(localStorage)
    if (!who || !mine || who !== mine) continue
    try {
      storage.setItem(OWN_ACCESS_TOKEN_KEY, legacy)
      const refresh = storage.getItem('refresh_token')
      if (refresh) storage.setItem(REFRESH_TOKEN_KEY, refresh)
      const valid = storage.getItem('token_valid_seconds')
      if (valid) storage.setItem(TOKEN_VALID_SECONDS_KEY, valid)
    } catch { /* storage blocked */ }
    return true
  }
  return false
}

/** JWT `exp` in epoch-ms, or null when the token is not a decodable JWT. */
export function tokenExpiryMs(token) {
  const payload = decodeJwtPayload(token)
  if (!payload) return null
  const exp = Number(payload.exp)
  return Number.isFinite(exp) && exp > 0 ? exp * 1000 : null
}

/**
 * Build the cookie string. A falsy token (or one whose `exp` has passed)
 * yields a deletion cookie. Host-only (no Domain), Path=/, SameSite=Lax,
 * Secure over https, Expires = JWT exp (session cookie when undecodable).
 */
export function buildSessionCookie(token, { secure = false, now = Date.now() } = {}) {
  const tail = `; Path=/; SameSite=Lax${secure ? '; Secure' : ''}`
  const expMs = token ? tokenExpiryMs(token) : null
  if (!token || (expMs !== null && expMs <= now)) {
    return `${SESSION_COOKIE_NAME}=; Expires=Thu, 01 Jan 1970 00:00:00 GMT; Max-Age=0${tail}`
  }
  const expires = expMs !== null ? `; Expires=${new Date(expMs).toUTCString()}` : ''
  return `${SESSION_COOKIE_NAME}=${encodeURIComponent(token)}${expires}${tail}`
}

function cookieContext() {
  const secure = typeof location !== 'undefined' && location.protocol === 'https:'
  const host = (typeof location !== 'undefined' && location.hostname) || ''
  return { secure, host }
}

function write(cookieStrings) {
  try {
    if (typeof document === 'undefined') return
    for (const str of cookieStrings) document.cookie = str
  } catch {
    // Cookies blocked/unavailable — the SPA itself still works off storage.
  }
}

/**
 * Put the origin in the single intended cookie state: the other accepted
 * names gone (host-only AND every `Domain=` variant — a parent-domain cookie
 * survives a host-only expiry), any `Domain=`-scoped twin of our own name
 * gone, and `cs_access_token` set to `token` (or deleted when falsy).
 *
 * Written last so the authoritative host-only cookie is the final word.
 */
function syncSessionCookie(token) {
  const { secure, host } = cookieContext()
  write([
    ...allCookieDeletions({ host, secure, except: SESSION_COOKIE_NAME }),
    ...cookieDeletions(SESSION_COOKIE_NAME, { host, secure }).slice(1), // Domain= variants only
    buildSessionCookie(token, { secure })
  ])
}

/** Delete every cookie name the dashboard accepts, in every Domain variant. */
function clearAcceptedCookies() {
  const { secure, host } = cookieContext()
  write([
    ...allCookieDeletions({ host, secure, except: SESSION_COOKIE_NAME }),
    ...cookieDeletions(SESSION_COOKIE_NAME, { host, secure }).slice(1),
    buildSessionCookie(null, { secure })
  ])
}

function removeEverywhere(key) {
  try { localStorage.removeItem(key) } catch { /* storage blocked */ }
  try { sessionStorage.removeItem(key) } catch { /* storage blocked */ }
}

/**
 * Wipe every artefact of a session: all SESSION_STORAGE_KEYS from BOTH
 * storages, the remember flag, and all three accepted cookie names in every
 * Domain variant.
 *
 * Called on logout AND immediately before a login writes its first token, so
 * a new session can never inherit anything from the previous identity — not
 * a refresh token (which the 401 interceptor would happily spend), not the
 * persisted vuex blob (which carries the user object the navbar renders and
 * the tokens `initializeAuth` refreshes), not a cookie this app does not
 * itself write.
 *
 * The remember flag goes too: the login path calls setRemember() AFTER this,
 * so the new session's choice — not the previous user's — decides which
 * storage the new tokens land in.
 */
export function clearSession() {
  for (const key of SESSION_STORAGE_KEYS) removeEverywhere(key)
  removeEverywhere(REMEMBER_FLAG_KEY)
  clearAcceptedCookies()
}

/**
 * App start: re-assert the cookie from whatever token survived in storage
 * ("remember me" restore), or clear every accepted cookie when the token is
 * absent/expired.
 *
 * It only touches COOKIES. It used to go through writeToken(), which also
 * re-routes the token between localStorage and sessionStorage according to
 * the (single, global) remember flag — so a tab booting after another tab
 * flipped that flag would migrate its stored token into the other storage.
 * Nothing needs that, and it is one of the ways two tabs ended up populating
 * both storages at once.
 */
export function restoreSessionCookie() {
  migrateLegacySessionKeys()
  const token = readToken(ACCESS_TOKEN_KEY)
  const expMs = token ? tokenExpiryMs(token) : null
  if (token && (expMs === null || expMs > Date.now())) {
    syncSessionCookie(token)
  } else {
    // Expired/absent: drop the cookies only. The stored (expired) token is
    // left for initializeAuth's refresh flow, which rewrites both on success.
    clearAcceptedCookies()
  }
}

export function setRemember(remember) {
  if (remember) {
    localStorage.setItem(REMEMBER_FLAG_KEY, 'true')
  } else {
    localStorage.removeItem(REMEMBER_FLAG_KEY)
  }
}

export function isRemembered() {
  return localStorage.getItem(REMEMBER_FLAG_KEY) === 'true'
}

export function writeToken(key, value) {
  if (isRemembered()) {
    localStorage.setItem(key, value)
    sessionStorage.removeItem(key)
  } else {
    sessionStorage.setItem(key, value)
    localStorage.removeItem(key)
  }
  if (key === ACCESS_TOKEN_KEY) syncSessionCookie(value)
}

/**
 * Read a session value from either storage — but fail closed when the two
 * disagree.
 *
 * This used to be `localStorage.getItem(k) || sessionStorage.getItem(k)`, so
 * localStorage won every disagreement regardless of which copy was newer or
 * less privileged. A disagreement means the origin is holding two sessions;
 * resolving it by preference is how a stale privileged token can be handed to
 * the request interceptors. Returning nothing makes the ambiguous state a
 * visible sign-out instead of a silent identity swap — the same fail-closed
 * rule the dashboard adopted for its cookies in 0.27.1.
 */
export function readToken(key) {
  const fromLocal = localStorage.getItem(key)
  const fromSession = sessionStorage.getItem(key)
  if (storagesDisagree(fromLocal, fromSession)) return null
  return fromLocal || fromSession
}

export function clearToken(key) {
  localStorage.removeItem(key)
  sessionStorage.removeItem(key)
  if (key === ACCESS_TOKEN_KEY) syncSessionCookie(null)
}

/**
 * Storage adapter for vuex-persist. Same semantics as the helpers above
 * — picks localStorage or sessionStorage based on the remember flag, and
 * fails closed on a disagreement (the persisted auth module is a credential:
 * it carries the user object the navbar renders and the tokens
 * `initializeAuth` refreshes).
 */
export const dynamicVuexStorage = {
  getItem(key) {
    const fromLocal = localStorage.getItem(key)
    const fromSession = sessionStorage.getItem(key)
    if (storagesDisagree(fromLocal, fromSession)) return null
    return fromLocal || fromSession
  },
  setItem(key, value) {
    if (isRemembered()) {
      localStorage.setItem(key, value)
      sessionStorage.removeItem(key)
    } else {
      sessionStorage.setItem(key, value)
      localStorage.removeItem(key)
    }
  },
  removeItem(key) {
    localStorage.removeItem(key)
    sessionStorage.removeItem(key)
  },
}
